
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from "@/integrations/supabase/client";
import { Link, useNavigate } from 'react-router-dom';
import SideImage from "../components/SideImage";
import { Key, User } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const HERO_IMAGE = "/lovable-uploads/b93b5bad-cffe-4247-aa30-5fa8a0d5cc9a.png";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const userType = session.user.user_metadata?.user_type || 'tenant';
        if (userType === 'landlord') {
          navigate("/owner-dashboard");
        } else {
          navigate("/");
        }
      }
    };
    checkSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setLoading(true);

    try {
      // أولاً: التحقق من وجود المشرف في جدول kanfadmin
      const { data: adminCheck } = await supabase
        .from('kanfadmin')
        .select('*')
        .eq('email', email)
        .eq('is_active', true)
        .maybeSingle();

      if (adminCheck) {
        // التحقق من كلمة المرور للمشرف باستخدام PostgreSQL crypt
        const { data: passwordCheck, error: passwordError } = await supabase.rpc('verify_admin_password', {
          input_email: email,
          input_password: password
        });

        if (passwordError) {
          console.error('Password verification error:', passwordError);
          setMsg("حدث خطأ في التحقق من كلمة المرور");
          toast({
            title: "خطأ في النظام",
            description: "حدث خطأ في التحقق من كلمة المرور",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }

        if (passwordCheck) {
          // تحديث آخر دخول للمشرف
          await supabase
            .from('kanfadmin')
            .update({ last_login: new Date().toISOString() })
            .eq('id', adminCheck.id);

          // حفظ بيانات المشرف في localStorage
          localStorage.setItem('admin_session', JSON.stringify({
            id: adminCheck.id,
            email: adminCheck.email,
            full_name: adminCheck.full_name,
            is_super_admin: adminCheck.is_super_admin,
            permissions: adminCheck.permissions
          }));

          setMsg("مرحباً بك في لوحة التحكم! سيتم تحويلك...");
          toast({
            title: "مرحباً بك مشرف النظام",
            description: "تم تسجيل الدخول بنجاح. جاري تحويلك للوحة التحكم.",
            variant: "default"
          });

          setTimeout(() => {
            navigate("/admin");
          }, 1200);
          return;
        } else {
          setMsg("حدث خطأ: البريد الإلكتروني أو كلمة المرور غير صحيحة");
          toast({
            title: "فشل تسجيل الدخول",
            description: "البريد الإلكتروني أو كلمة المرور غير صحيحة",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
      }

      // المتابعة مع تسجيل الدخول العادي لغير المشرفين
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });

      if (error) {
        console.error('Login error:', error);
        
        let errorMessage = "حدث خطأ غير متوقع";
        
        if (error.message === "Invalid login credentials") {
          errorMessage = "البريد الإلكتروني أو كلمة المرور غير صحيحة";
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage = "يجب تفعيل البريد الإلكتروني أولاً. تحقق من صندوق الوارد الخاص بك";
        } else if (error.message.includes("Too many requests")) {
          errorMessage = "تم إرسال طلبات كثيرة. حاول مرة أخرى بعد قليل";
        } else {
          errorMessage = error.message;
        }

        setMsg("حدث خطأ: " + errorMessage);
        toast({
          title: "فشل تسجيل الدخول",
          description: errorMessage,
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      if (data.user && data.session) {
        // تفعيل الحساب تلقائياً بعد تأكيد البريد
        try {
          await supabase.rpc('activate_account_after_email_verification', {
            p_user_id: data.user.id
          });
        } catch (activationError) {
          // Silent handling of activation check
        }
        
        // أولاً: التحقق من وجود المستخدم في جدول kanfadmin
        const { data: adminData, error: adminError } = await supabase
          .from('kanfadmin')
          .select('*')
          .eq('email', data.user.email || '')
          .eq('is_active', true)
          .maybeSingle();

        if (adminData && !adminError) {
          // المستخدم مشرف، تحديث آخر دخول وتوجيهه لصفحة الإدارة
          await supabase
            .from('kanfadmin')
            .update({ last_login: new Date().toISOString() })
            .eq('id', adminData.id);

          setMsg("مرحباً بك في لوحة التحكم! سيتم تحويلك...");
          toast({
            title: "مرحباً بك مشرف النظام",
            description: "تم تسجيل الدخول بنجاح. جاري تحويلك للوحة التحكم.",
            variant: "default"
          });

          setTimeout(() => {
            navigate("/admin");
          }, 1200);
          return;
        }

        // التحقق من نوع المستخدم ووجود ملفه الشخصي
        const userType = data.user.user_metadata?.user_type || 'tenant';
        let profileExists = false;
        let isActive = true;
        
        if (userType === 'tenant') {
          const { data: profile } = await supabase
            .from('tenants')
            .select('*')
            .eq('user_id', data.user.id)
            .single();
          profileExists = !!profile;
        } else if (userType === 'landlord') {
          const { data: profile } = await supabase
            .from('landlords')
            .select('*')
            .eq('user_id', data.user.id)
            .single();
          profileExists = !!profile;
          isActive = profile?.is_active ?? false;
        } else if (userType === 'service_provider') {
          const { data: profile } = await supabase
            .from('service_providers')
            .select('*')
            .eq('user_id', data.user.id)
            .single();
          profileExists = !!profile;
          isActive = profile?.is_active ?? false;
        }

        // التحقق من حالة التفعيل (تأكيد البريد)
        if (!isActive) {
          setMsg("يجب تأكيد بريدك الإلكتروني أولاً. تحقق من صندوق الوارد.");
          toast({
            title: "تأكيد البريد مطلوب",
            description: "يرجى تأكيد بريدك الإلكتروني من خلال الرابط المرسل إليك.",
            variant: "destructive"
          });
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        // إذا لم يوجد ملف شخصي، أنشئه
        if (!profileExists && userType !== 'service_provider') {
          const tableName = userType === 'tenant' ? 'tenants' : 'landlords';
          const { error: insertError } = await supabase
            .from(tableName)
            .insert([
              {
                user_id: data.user.id,
                full_name: data.user.user_metadata?.full_name || '',
                email: data.user.email || '',
                phone: data.user.user_metadata?.phone || ''
              }
            ]);

          if (insertError) {
            console.error('Profile creation error:', insertError);
          }
        }

        setMsg("تم تسجيل الدخول بنجاح! سيتم تحويلك...");
        toast({
          title: "تم تسجيل الدخول",
          description: "تم تسجيل الدخول بنجاح. جاري تحويلك.",
          variant: "default"
        });

        // توجيه المستخدم حسب نوعه
        setTimeout(() => {
          if (userType === 'landlord') {
            navigate("/owner-dashboard");
          } else if (userType === 'service_provider') {
            navigate("/service-provider-dashboard");
          } else {
            // الباحث يتم توجيهه للصفحة الرئيسية
            navigate("/");
          }
        }, 1200);
      }
    } catch (error) {
      console.error('Unexpected login error:', error);
      setMsg("حدث خطأ غير متوقع. حاول مرة أخرى.");
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع. حاول مرة أخرى.",
        variant: "destructive"
      });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-['Tajawal',sans-serif] bg-gradient-to-br from-blue-50 to-blue-100">
      <SideImage 
        imageUrl={HERO_IMAGE} 
        alt="أحد الباحثين عن السكن يعمل على كمبيوتر في غرفة مشرقة" 
      />

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white shadow-2xl rounded-3xl w-full max-w-md p-8 md:p-10">
          <Link to="/" className="flex items-center justify-center gap-2 mb-5">
            <img 
              src="/lovable-uploads/b93b5bad-cffe-4247-aa30-5fa8a0d5cc9a.png" 
              alt="شعار كنف" 
              className="h-12 w-auto"
            />
          </Link>
          
          <h2 className="mb-2 text-center text-3xl font-bold text-gray-900">
            تسجيل الدخول إلى حسابك
          </h2>
          
          <p className="mb-4 text-center text-sm text-gray-600">
            أو{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
              أنشئ حساب جديد
            </Link>
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5" dir="rtl">
            <div>
              <Label htmlFor="email" className="block mb-1">البريد الإلكتروني</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="أدخل بريدك الإلكتروني"
                  required
                  className="text-right pr-10"
                />
                <User className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="block mb-1">كلمة المرور</Label>
              <div className="relative">
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور"
                  required
                  className="text-right pr-10"
                />
                <Key className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"/>
              </div>
            </div>

            {msg && (
              <div className={`text-center mt-2 text-sm font-semibold p-2 rounded ${
                msg.includes('خطأ') || msg.includes('فشل')
                  ? 'text-red-600 bg-red-50'
                  : 'text-green-600 bg-green-50'
              }`}>
                {msg}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 transition" 
              disabled={loading}
            >
              {loading ? "جاري تسجيل الدخول..." : "تسجيل الدخول"}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <span className="text-gray-500">ليس لديك حساب؟ </span>
            <Link to="/register" className="text-blue-600 hover:underline">سجل الآن</Link>
          </div>

          <div className="mt-6 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-800 text-center">
              إذا لم تستطع الدخول بسبب عدم تفعيل البريد، تحقق من صندوق الوارد أو المجلد المهمل
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
